import "./_style.less";
import { useTranslation } from "react-i18next";
import { useDisclosure } from "../../../../hooks/use-disclosure";
import { Col, Modal, Row } from "antd";
import {
  VuiButton,
  VuiCalendarReschedule,
  VuiSecondaryTitleWrapper,
} from "../../../../@vendor/components";
import { CloseIcon } from "../../../../components";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Schedule } from "../../../../models/Schedule";
import moment, { Moment } from "moment";
import clsx from "clsx";

interface IScheduleCalendar {
  data: Schedule[];
  selectedData?: Schedule[];
  onSelectDate?: (dates: Schedule[]) => void;
  isMultiple?: boolean;
}

const ScheduleCalendar = ({
  data,
  selectedData = [],
  onSelectDate,
  isMultiple = false,
}: IScheduleCalendar) => {
  const { t } = useTranslation();
  const { onClose, onOpen, isOpen } = useDisclosure();
  const [currData, setCurrData] = useState<Schedule[]>([]);

  const handleSelectMultipleDate = useCallback(
    (date: Moment) => {
      const formattedDate = date.format("YYYY-MM-DD");
      const findSchedule = data.find(
        (calendar) => calendar.date === formattedDate
      );

      const indexDate = currData.findIndex(
        (calendar) => calendar.date === formattedDate
      );
      if (findSchedule) {
        if (indexDate === -1) {
          if (!isMultiple) {
            setCurrData([findSchedule]);
          } else {
            setCurrData([...currData, findSchedule]);
          }
        } else {
          setCurrData(currData?.filter((sd: any) => sd.date !== formattedDate));
        }
      }
    },
    [data, currData, isMultiple]
  );

  const onApply = useCallback(() => {
    if (onSelectDate) onSelectDate(currData);
    setCurrData([]);
    onClose();
  }, [currData, onSelectDate]);

  const scheduleDates = useMemo(() => {
    return data.slice(0, 6).map((d) => d?.date);
  }, [data]);

  const otherScheduleDates = useMemo(() => {
    return selectedData?.filter((s) => !scheduleDates.includes(s.date));
  }, [scheduleDates, selectedData]);

  useEffect(() => {
    if (selectedData && isOpen) {
      setCurrData(selectedData);
    }
  }, [selectedData, isOpen]);

  return (
    <div className="schedule-calendar">
      <Modal
        footer={null}
        closable={false}
        visible={isOpen}
        className="default-modal"
        title={null}
        centered
      >
        <div className="default-modal-header">
          <VuiSecondaryTitleWrapper
            title={t("common.text.chooseADate")}
            level={4}
          >
            <CloseIcon onClick={onClose} />
          </VuiSecondaryTitleWrapper>
        </div>

        <div
          style={{
            marginBottom: 20,
          }}
        >
          <div
            style={{
              border: "1px solid #E0E2E3",
              borderRadius: 10,
              marginBottom: 15,
            }}
          >
            <VuiCalendarReschedule
              onSelectDate={handleSelectMultipleDate}
              data={data}
              selectedDates={currData?.map((sd) => sd?.date)}
              // disabledDate={(d) => {}}
            />
          </div>

          <div className="schedule-calendar-helpers">
            <div className="schedule-calendar-helper">
              <span className="bullet available" />
              <span>{t("common.text.available")}</span>
            </div>
            <div className="schedule-calendar-helper">
              <span className="bullet chosen" />
              <span>{t("common.text.chosen")}</span>
            </div>
            <div className="schedule-calendar-helper">
              <span className="line" />
              <span>{t("common.text.today")}</span>
            </div>
          </div>
        </div>

        <Row gutter={[12, 12]}>
          <Col md={12} xs={24}>
            <VuiButton
              buttonProps={{ onClick: onClose }}
              label={t("common.button.cancel")}
            />
          </Col>
          <Col md={12} xs={24}>
            <VuiButton
              buttonProps={{ type: "primary", onClick: onApply }}
              label={t("common.button.apply")}
            />
          </Col>
        </Row>
      </Modal>

      <div
        className={clsx("schedule-calendar-trigger", {
          active: otherScheduleDates?.length > 0,
        })}
        onClick={onOpen}
      >
        <div>{t("common.text.otherDates")}</div>
        {otherScheduleDates.length === 0 ? null : otherScheduleDates.length ===
          1 ? (
          <div>
            ({moment(otherScheduleDates[0]?.date).format("ddd, DD MMM")})
          </div>
        ) : (
          <div className="schedule-calendar-trigger-text">
            {otherScheduleDates.length}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleCalendar;
